/**
 * Cart drawer rewards: value-based milestone bar + automatic free products.
 *
 * Milestones (all based on the POST-DISCOUNT value of NON-restricted items,
 * excluding the gifts themselves):
 *   ₹400  (40000 paise)  -> Free Shipping
 *   ₹650  (65000 paise)  -> free gift (auto-added at ₹0)
 *   ₹1000 (100000 paise) -> FRESH AF 100g free (auto-added at ₹0)
 *
 * Gifts STACK: at ₹1000 the customer keeps the ₹650 gift AND gets FRESH AF.
 *
 * The milestone bar (`.drawer-progress`) lives OUTSIDE `<cart-drawer-items>`, so
 * it is NOT re-rendered when the cart drawer section reloads — every visual change
 * to the bar must happen here in JS. The gift line items and the price footer,
 * by contrast, ARE inside `<cart-drawer-items>` and re-render server-side.
 *
 * Gift behaviour, evaluated per tier on every sync:
 *   - eligible total >= threshold and gift not in cart -> add it (₹0)
 *   - eligible total <  threshold and gift in cart     -> remove it
 *   Gifts are excluded from the eligible total, so adding/removing one never
 *   changes the total — this prevents add/remove oscillation.
 *
 * Adding/changing a tier means editing BOTH TIERS below and the matching block
 * in snippets/cart-drawer.liquid (which renders the first paint server-side).
 *
 * Product-level overrides (product metafields, read via /cart?view=rewards —
 * templates/cart.rewards.liquid — since /cart.js exposes no metafields):
 *   custom.exclude_bau_milestones -> while such a product is in the cart, the
 *     gift tiers are withheld and the bar is hidden. Free shipping is untouched.
 *   custom.product_freebie        -> that ₹0 variant is auto-added once (qty 1),
 *     tagged `_product_freebie`, and removed when the product leaves the cart.
 */

const CART_REWARDS = {
  // Ascending by threshold. `variantId` is the ₹0 gift line auto-added once the
  // tier unlocks; null means the tier grants no cart line — either because the
  // benefit isn't one (free shipping) or because the product isn't wired up yet.
  // A null-variant tier still renders on the bar but never touches the cart.
  //
  // `bgAttr` is the data-attribute on .drawer-progress-container holding that
  // tier's progress-fill image (set via asset_url in cart-drawer.liquid).
  TIERS: [
    {
      threshold: 40000, // ₹400
      variantId: null,
      icon: 'drawer-free-shipping.png',
      iconAlt: 'Free Shipping',
      bgAttr: 'bgShip',
      lockedMessage: (remaining) => `Add ₹${remaining} more to unlock Free Shipping`,
      unlockedNote: 'Free Shipping unlocked!',
    },
    {
      // Mini Face Moisturizer — product 7310273511558.
      // Keep this ID in sync with mid_gift_variant_id in snippets/cart-drawer.liquid,
      // and the copy below in sync with mid_gift_label there.
      threshold: 65000, // ₹650
      variantId: 44201324904582,
      icon: 'drawer-gift.png',
      iconAlt: 'Free Mini Face Moisturizer',
      bgAttr: 'bgMid',
      lockedMessage: (remaining) => `Add ₹${remaining} more to get a FREE Mini Face Moisturizer`,
      unlockedNote: 'Free Mini Face Moisturizer unlocked!',
    },
    {
      threshold: 100000, // ₹1000
      variantId: 44115348455558,
      icon: 'drawer-gift.png',
      iconAlt: 'Free Fresh AF 100g',
      bgAttr: 'bgGift',
      lockedMessage: (remaining) => `Add ₹${remaining} more to get a FREE Fresh AF 100g`,
      unlockedNote: `You've unlocked your FREE Fresh AF 100g!`,
    },
  ],
};

class CartRewards {
  constructor() {
    this.syncing = false;
    this.pendingSync = false;
    this.syncTimer = null;
    this.staleRenderFixes = 0;
    // Product-freebie entries keyed by variant ID, so a parked one stays parked.
    this.productFreebies = new Map();
    this.sync();
    this.setupEventListeners();
  }

  setupEventListeners() {
    ['cart-drawer:open', 'cart:updated', 'cart:refresh'].forEach((event) => {
      document.addEventListener(event, () => this.scheduleSync());
    });

    this.subscribeToCartUpdates();
  }

  // Subscribe to the theme's pub/sub cart channel. This script loads `async`, so the
  // deferred pubsub.js may not have defined `subscribe` yet when we first run. The
  // product-form add-to-cart notifies ONLY via this channel, so if we gave up here
  // the freebie would never be added on that path. Retry until it's available.
  subscribeToCartUpdates(attempt) {
    attempt = attempt || 0;
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Ignore the cartUpdate we publish ourselves after adding/removing the
        // freebie, otherwise we'd re-enter sync() needlessly.
        if (event && event.source === 'cart-rewards') return;
        this.scheduleSync();
      });
      return;
    }
    if (attempt < 50) {
      setTimeout(() => this.subscribeToCartUpdates(attempt + 1), 100);
    }
  }

  // Debounce reconciliation so it runs once AFTER the theme's own renders settle.
  // Adding a product (product-form) fires a burst of events and then a full drawer
  // re-render from a response captured BEFORE the freebie existed. Running our add +
  // render last makes it the final word, so a stale full-render can't overwrite the
  // freebie back out of the cart display.
  scheduleSync() {
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => this.sync(), 300);
  }

  // Feature flag (settings.cart_milestone_enable), surfaced on <cart-drawer> as a
  // data attribute. Defaults to enabled if the element/attribute is absent.
  isEnabled() {
    const el = document.querySelector('cart-drawer');
    return !el || el.dataset.milestoneEnabled !== 'false';
  }

  // ── Cart math ──────────────────────────────────────────────────────────────

  // Variant IDs of restricted/birthday products, read from the server-rendered
  // DOM (cart.js exposes no product tags). Mirrors the `restricted` guard in
  // cart-drawer.liquid.
  getRestrictedVariantIds() {
    const ids = new Set();
    document.querySelectorAll('tr.cart-item[data-restricted="true"]').forEach((row) => {
      const el = row.querySelector('[data-variant-id]');
      if (el) ids.add(String(el.dataset.variantId));
    });
    return ids;
  }

  // Tiers that actually add a ₹0 line to the cart.
  giftTiers() {
    return CART_REWARDS.TIERS.filter((tier) => tier.variantId);
  }

  isGiftVariant(variantId) {
    return this.giftTiers().some((tier) => tier.variantId === variantId);
  }

  // A line auto-added from a product's custom.product_freebie. Matched by property,
  // not variant, so the same product bought at full price is a normal line.
  isProductFreebieLine(item) {
    return !!(item.properties && item.properties._product_freebie);
  }

  // Any auto-added line: a milestone gift or a product freebie.
  isAutoGiftLine(item) {
    return this.isGiftVariant(item.variant_id) || this.isProductFreebieLine(item);
  }

  productFreebieEntry(variantId) {
    if (!this.productFreebies.has(variantId)) {
      this.productFreebies.set(variantId, { variantId, productFreebie: true });
    }
    return this.productFreebies.get(variantId);
  }

  // Metafield-driven state for the current cart. Null when the view can't be read,
  // in which case sync() leaves product freebies alone rather than strip them.
  async fetchProductRewards() {
    try {
      // No `Accept: application/json` header: Shopify answers that with plain
      // cart JSON and ignores `view`, so the metafield fields would never arrive.
      const res = await fetch('/cart?view=rewards');
      if (!res.ok) throw new Error(`status ${res.status}`);
      return await res.json();
    } catch (error) {
      console.error('CartRewards: could not read /cart?view=rewards:', error);
      return null;
    }
  }

  // Post-discount total (paise) of non-restricted items, excluding every gift.
  computeEligibleTotal(cart, restrictedIds) {
    let total = 0;
    cart.items.forEach((item) => {
      if (this.isAutoGiftLine(item)) return;
      if (restrictedIds.has(String(item.variant_id))) return;
      total += item.final_line_price || 0;
    });
    return total;
  }

  nonRestrictedCount(cart, restrictedIds) {
    return cart.items.reduce((n, item) => {
      if (this.isAutoGiftLine(item)) return n;
      if (restrictedIds.has(String(item.variant_id))) return n;
      return n + item.quantity;
    }, 0);
  }

  async sync() {
    // Coalesce overlapping calls. If a sync is already running, don't drop this
    // request — flag it and re-run once the current one finishes. Without this, an
    // event that fires mid-sync is lost, which is why the freebie added only
    // "sometimes": a sync that read the cart before it crossed the threshold would
    // swallow the follow-up event that should have triggered the add.
    if (this.syncing) {
      this.pendingSync = true;
      return;
    }
    this.syncing = true;
    try {
      const [cart, productRewards] = await Promise.all([
        fetch('/cart.js').then((res) => res.json()),
        this.fetchProductRewards(),
      ]);

      // Feature off (settings.cart_milestone_enable): the milestone gifts are
      // withheld — and any already present removed — exactly as if a product had
      // excluded them. Product freebies are not milestones and still apply.
      const enabled = this.isEnabled();
      const excluded = !enabled || !!(productRewards && productRewards.exclude_bau_milestones);

      const restrictedIds = this.getRestrictedVariantIds();
      const eligibleTotal = this.computeEligibleTotal(cart, restrictedIds);

      // Update the bar first — it is not part of any re-rendered section, and the
      // eligible total already excludes the gifts so it is correct regardless of
      // which gift is about to be added or removed.
      if (enabled) this.updateBar(eligibleTotal, cart, restrictedIds, excluded);

      // Then reconcile every gift. This may add/remove lines and trigger a
      // server-side re-render of the items + footer.
      const changed = await this.reconcileGifts(eligibleTotal, cart, excluded, productRewards);

      if (!changed) this.fixStaleGiftRender(cart, productRewards);
    } catch (error) {
      console.error('CartRewards sync failed:', error);
    } finally {
      this.syncing = false;
      // A cart change arrived while we were busy — re-run once with fresh state.
      // (Our own freebie-driven refresh uses source 'cart-rewards', which the
      // subscriber ignores, so this can't loop forever.)
      if (this.pendingSync) {
        this.pendingSync = false;
        this.sync();
      }
    }
  }

  // ── Gift auto add / remove ───────────────────────────────────────────────────

  // Gifts stack, so each tier is judged independently: a tier's gift belongs in
  // the cart whenever its own threshold is met. Adds and removes are batched into
  // one request each, so crossing several tiers at once (or dropping back below
  // them) costs a single re-render rather than one per gift.
  // `excluded` withholds every tier (product exclusion or feature flag off).
  // Product freebies are reconciled in the same pass, so they share the batching.
  // Returns true when a cart mutation was performed.
  async reconcileGifts(eligibleTotal, cart, excluded, productRewards) {
    const toAdd = [];
    const removeKeys = [];

    this.giftTiers().forEach((tier) => {
      // A product freebie may be the same variant as a tier gift (e.g. Fresh AF);
      // that line belongs to reconcileProductFreebies, not to the tier.
      const line = cart.items.find(
        (i) => i.variant_id === tier.variantId && !this.isProductFreebieLine(i),
      );
      const shouldHave = !excluded && eligibleTotal >= tier.threshold;

      // A line the customer is being CHARGED for is not a gift, whatever the tier
      // config says — the variant isn't priced ₹0, or the discount meant to zero it
      // didn't apply. Take it out and park the tier instead of re-adding it on the
      // next sync, otherwise the customer is billed for something they didn't choose
      // and can't get rid of. Same test the Liquid uses for the "Free" label.
      if (line && line.final_line_price > 0) {
        removeKeys.push(line.key);
        this.parkTier(
          tier,
          `its cart line is being charged (${line.final_line_price}), so it is not a gift. ` +
            `Check the variant is priced ₹0, or that the discount which zeroes it applied.`,
        );
        return;
      }

      // A parked tier is one already rejected this page (see parkTier); don't keep
      // re-requesting it. Removal still applies, so a parked gift that somehow made
      // it into the cart can still be taken out.
      if (shouldHave && !line) {
        if (!tier.unavailable) toAdd.push(tier);
      } else if (!shouldHave && line) {
        removeKeys.push(line.key);
      }
    });

    if (productRewards) this.reconcileProductFreebies(cart, productRewards, toAdd, removeKeys);

    if (!toAdd.length && !removeKeys.length) return false;

    // Removals first: if a tier both lost and gained a gift in the same pass the
    // cart ends in the intended state either way, but removing first keeps the
    // line count from briefly overshooting.
    //
    // Report whether a mutation actually LANDED, not merely that one was attempted.
    // sync() skips the stale-render check when this returns true, and a pass whose
    // add failed outright is exactly the pass most likely to be displaying a gift
    // row the cart doesn't have.
    let changed = false;
    if (removeKeys.length && (await this.removeGifts(removeKeys))) changed = true;
    if (toAdd.length && (await this.addGifts(toAdd))) changed = true;
    return changed;
  }

  // One line per wanted freebie (qty 1 however many combos are in the cart); a
  // freebie whose product left the cart, or a duplicate line, is removed. Pushes
  // into reconcileGifts' batches rather than mutating the cart itself.
  reconcileProductFreebies(cart, productRewards, toAdd, removeKeys) {
    const wanted = new Set((productRewards.product_freebies || []).map(Number));
    const kept = new Set();

    cart.items.forEach((item) => {
      if (!this.isProductFreebieLine(item)) return;
      const entry = this.productFreebieEntry(item.variant_id);

      // Charged freebie: same rule as the tiers above.
      if (item.final_line_price > 0) {
        removeKeys.push(item.key);
        this.parkTier(
          entry,
          `its cart line is being charged (${item.final_line_price}), so it is not a gift. ` +
            `Check custom.product_freebie points at a ₹0 variant.`,
        );
        return;
      }
      if (!wanted.has(item.variant_id) || kept.has(item.variant_id)) {
        removeKeys.push(item.key);
        return;
      }
      kept.add(item.variant_id);
    });

    wanted.forEach((variantId) => {
      if (kept.has(variantId)) return;
      const entry = this.productFreebieEntry(variantId);
      if (!entry.unavailable) toAdd.push(entry);
    });
  }

  // The drawer re-render can be served from a cart snapshot captured before our
  // add/remove landed, leaving a gift row displayed that isn't in the cart (or
  // vice versa). Detect the mismatch per gift and force one more render.
  fixStaleGiftRender(cart, productRewards) {
    const itemsEl = document.querySelector('cart-drawer-items');
    if (!itemsEl) return;

    const isRendered = (variantId) =>
      !!itemsEl.querySelector(`[data-quantity-variant-id="${variantId}"]`);
    const freebieIds = (productRewards && productRewards.product_freebies) || [];

    const stale =
      this.giftTiers().some((tier) => {
        // Skipped when a product freebie shares the variant: its row would read as
        // the tier's, and the mismatch would force pointless re-renders.
        if (cart.items.some((i) => i.variant_id === tier.variantId && this.isProductFreebieLine(i))) return false;
        const inCart = cart.items.some((i) => i.variant_id === tier.variantId);
        return inCart !== isRendered(tier.variantId);
      }) ||
      freebieIds.map(Number).some((variantId) => {
        const inCart = cart.items.some((i) => i.variant_id === variantId && this.isProductFreebieLine(i));
        return inCart && !isRendered(variantId);
      });

    if (!stale) {
      this.staleRenderFixes = 0;
      return;
    }
    if (this.staleRenderFixes >= 3) return;
    this.staleRenderFixes++;
    this.refreshDrawer();
  }

  cartRequestConfig(payload) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    };
  }

  // Resolves true only when the cart actually changed, so reconcileGifts can tell
  // sync() whether the stale-render check still needs to run.
  async addGifts(tiers) {
    if (typeof CartOperationLock !== 'undefined') CartOperationLock.lock();
    try {
      let result = await this.postGiftAdd(tiers);

      // Shopify rejects a batched add as a whole, so ONE bad variant would starve
      // the healthy gifts sent alongside it. Retry singly to isolate the culprit.
      if (!result.ok && result.clientError && tiers.length > 1) {
        let succeeded = null;
        for (const tier of tiers) {
          const single = await this.postGiftAdd([tier]);
          if (single.ok) succeeded = single;
          else if (single.clientError) this.parkTier(tier, this.refusedReason(single.detail));
        }
        if (!succeeded) return false;
        result = succeeded;
      } else if (!result.ok) {
        if (result.clientError) this.parkTier(tiers[0], this.refusedReason(result.detail));
        return false;
      }

      this.updateCartIcon(result.data.sections);
      this.refreshDrawer();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      if (typeof CartOperationLock !== 'undefined') CartOperationLock.unlock();
    }
  }

  // Resolves to { ok } rather than throwing, so a rejected variant is a decision
  // the caller can act on instead of an exception that abandons the whole pass.
  async postGiftAdd(tiers) {
    const url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    const res = await fetch(
      url,
      this.cartRequestConfig({
        items: tiers.map((tier) => ({
          id: tier.variantId,
          quantity: 1,
          properties: tier.productFreebie
            ? { _freebie: 'true', _product_freebie: 'true' }
            : { _freebie: 'true' },
        })),
        sections: ['cart-icon-bubble'],
      }),
    );

    if (res.ok) return { ok: true, data: await res.json() };

    // Surface Shopify's reason (e.g. unpublished product, out of stock) so a
    // silent failure is debuggable from the console.
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch (e) {
      /* response had no JSON body */
    }
    const ids = tiers.map((tier) => tier.variantId).join(', ');
    console.error(`Failed to add gift (${res.status}) for variant(s) ${ids}: ${detail}`);
    return { ok: false, clientError: res.status >= 400 && res.status < 500, detail };
  }

  // Stop trying a tier for the rest of the page. Used when retrying would just
  // repeat the same failure: Shopify refusing the variant (unpublished, sold out),
  // or the gift arriving with a price on it. A republish, restock or fixed price is
  // picked up on the next page load.
  refusedReason(detail) {
    return (
      `Shopify refused it: ${detail}. Usual causes: the product is not published ` +
      `to the Online Store, or it is sold out.`
    );
  }

  parkTier(tier, reason) {
    if (tier.unavailable) return;
    tier.unavailable = true;
    const label = tier.productFreebie ? 'product freebie' : `₹${tier.threshold / 100} gift`;
    console.warn(`[CartRewards] Parking the ${label} (variant ${tier.variantId}) for this page — ${reason}`);
  }

  // Batched removal via /cart/update.js (`updates` keyed by line key), so dropping
  // below several tiers at once is a single request and a single re-render.
  async removeGifts(lineKeys) {
    if (typeof CartOperationLock !== 'undefined') CartOperationLock.lock();
    try {
      const url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
      const updates = {};
      lineKeys.forEach((key) => {
        updates[key] = 0;
      });
      const res = await fetch(
        url,
        this.cartRequestConfig({ updates, sections: ['cart-icon-bubble'] }),
      );
      if (!res.ok) throw new Error(`Failed to remove gift(s) (${res.status})`);
      const data = await res.json();
      this.updateCartIcon(data.sections);
      this.refreshDrawer();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      if (typeof CartOperationLock !== 'undefined') CartOperationLock.unlock();
    }
  }

  updateCartIcon(sections) {
    const sectionHtml = sections && sections['cart-icon-bubble'];
    if (!sectionHtml) return;
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const fresh = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.shopify-section');
    if (fresh) bubble.innerHTML = fresh.innerHTML;
  }

  // Trigger the theme's drawer re-render (cart-drawer-items.onCartUpdate). Using a
  // distinct source lets our own subscriber skip the resulting event.
  refreshDrawer() {
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-rewards' });
    }
  }

  // ── Bar rendering ─────────────────────────────────────────────────────────────

  updateBar(eligibleTotal, cart, restrictedIds, excluded) {
    const dpText = document.getElementById('dp-text');
    const container = document.querySelector('.drawer-progress-container');
    const wrapper = document.querySelector('.drawer-progress');

    const hasNonGiftItems = cart.items.some((i) => !this.isAutoGiftLine(i));
    // A product excluding milestones hides the bar and its message entirely.
    if (!hasNonGiftItems || excluded) {
      if (wrapper) wrapper.style.display = 'none';
      return;
    }
    if (wrapper) wrapper.style.display = '';

    // All non-freebie items restricted -> hide the bar, show the restricted note.
    if (this.nonRestrictedCount(cart, restrictedIds) === 0) {
      if (dpText) {
        dpText.innerHTML = 'Offers are not applicable on restricted products';
        dpText.classList.add('restricted-msg');
        dpText.style.display = '';
      }
      if (container) container.style.display = 'none';
      return;
    }

    if (dpText) dpText.classList.remove('restricted-msg');
    if (container) container.style.display = '';

    const unlocked = CART_REWARDS.TIERS.map((tier) => eligibleTotal >= tier.threshold);

    this.updateBackground(unlocked);
    CART_REWARDS.TIERS.forEach((tier, i) => {
      this.updateIcon(`[data-threshold="${tier.threshold}"]`, unlocked[i], tier.icon, tier.iconAlt);
    });
    this.updateClasses(unlocked);
    this.updateText(eligibleTotal, unlocked);
  }

  // Stepped progress-fill background. The per-state image URLs come from the
  // container's data attributes (set via asset_url in cart-drawer.liquid), so they
  // always resolve. Set directly (no opacity-fade-via-setTimeout, which previously
  // left the bar stuck invisible when rapid cart updates fired within the fade window).
  // Uses the fill for the highest unlocked tier, walking down to the next lower
  // tier if that one's attribute is missing (a not-yet-uploaded fill image falls
  // back to a shorter fill rather than blanking the bar).
  updateBackground(unlocked) {
    const container = document.querySelector('.drawer-progress-container');
    if (!container) return;

    let url = container.dataset.bgEmpty;
    for (let i = CART_REWARDS.TIERS.length - 1; i >= 0; i--) {
      if (!unlocked[i]) continue;
      const candidate = container.dataset[CART_REWARDS.TIERS[i].bgAttr];
      if (candidate) {
        url = candidate;
        break;
      }
    }
    if (!url) return;

    container.style.backgroundImage = `url(${url})`;
    container.style.opacity = '1';
  }

  // Matches any icon filename the bar can currently display.
  iconFilePattern() {
    const files = new Set(CART_REWARDS.TIERS.map((tier) => tier.icon));
    files.add('drawer-lock.png');
    const names = [...files].map((file) => file.replace(/\.png$/, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    return new RegExp(`(${names.join('|')})\\.png`);
  }

  updateIcon(selector, isUnlocked, unlockedFile, altText) {
    const milestone = document.querySelector(selector);
    if (!milestone) return;

    const iconImg = milestone.querySelector('.milestone-icon-img');
    if (!iconImg) return;

    const targetFile = isUnlocked ? unlockedFile : 'drawer-lock.png';
    // Swap the filename within the EXISTING src so we keep the correct CDN base
    // (theme `assets/` URL + version). The pattern is built from the configured
    // tier icons so a new tier icon doesn't silently stop matching.
    const newSrc = iconImg.src.replace(this.iconFilePattern(), targetFile);
    // Always normalise visibility/scale (guards against a previous interrupted
    // animation leaving the icon at opacity:0), then swap the src if it changed.
    iconImg.style.transform = 'scale(1)';
    iconImg.style.opacity = '1';
    if (iconImg.src === newSrc) return;
    iconImg.src = newSrc;
    iconImg.alt = isUnlocked ? altText : 'Locked';
  }

  // Unlocked tiers read `completed`; every locked tier is `upcoming-milestone`,
  // and the lowest locked one additionally gets `next-milestone` (the bouncing icon).
  updateClasses(unlocked) {
    const nextIndex = unlocked.indexOf(false);

    CART_REWARDS.TIERS.forEach((tier, i) => {
      const el = document.querySelector(`[data-threshold="${tier.threshold}"]`);
      if (!el) return;

      el.classList.remove('next-milestone', 'upcoming-milestone', 'completed');
      if (unlocked[i]) {
        el.classList.add('completed');
        return;
      }
      el.classList.add('upcoming-milestone');
      if (i === nextIndex) el.classList.add('next-milestone');
    });
  }

  // "<last unlocked benefit> unlocked! Add ₹X more to get <next reward>", falling
  // back to just the next reward when nothing is unlocked yet, and to the top
  // tier's note once every tier is unlocked.
  updateText(eligibleTotal, unlocked) {
    const dpText = document.getElementById('dp-text');
    if (!dpText) return;

    const star = '<span class="dp-star"></span>';
    const nextIndex = unlocked.indexOf(false);
    let msg;

    if (nextIndex === -1) {
      msg = `${CART_REWARDS.TIERS[CART_REWARDS.TIERS.length - 1].unlockedNote} ${star}`;
    } else {
      const nextTier = CART_REWARDS.TIERS[nextIndex];
      const remaining = Math.ceil((nextTier.threshold - eligibleTotal) / 100);
      const note = nextIndex > 0 ? `${CART_REWARDS.TIERS[nextIndex - 1].unlockedNote} ` : '';
      msg = `${note}${nextTier.lockedMessage(remaining)} ${star}`;
    }

    dpText.innerHTML = msg;
    dpText.style.display = '';
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartRewards = new CartRewards();
  });
} else {
  window.cartRewards = new CartRewards();
}
